import React, { useContext, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import { navigationRef } from "../../../App";
import routes from "../../navigation/routes";
import { getPlan, currentPlanId, isPaid, nextPaidPlan } from "../data/plans";

/**
 * Sidebar plan widget, pinned to the bottom of the nav rail. Two states off the
 * same slot:
 *   - Free  → bold brand upgrade card (push to a paid plan).
 *   - Paid  → calm "· ACTIVE" status card with a Manage link.
 * Both route to the Plans page.
 */
function NtSidebarPlanCard() {
  const { theme } = useTheme();
  const context = useContext(UserContext);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const plan = getPlan(currentPlanId(context?.user));
  const goToPlans = () => navigationRef.current?.navigate(routes.plan);

  if (isPaid(plan)) {
    return (
      <View style={styles.wrap}>
        <View style={styles.paidCard}>
          <View style={[styles.badge, { backgroundColor: theme.green + "24", borderColor: theme.green + "59" }]}>
            <View style={[styles.badgeDot, { backgroundColor: theme.green }]} />
            <NtText style={[styles.badgeText, { color: theme.green }]}>
              {plan.name.toUpperCase()} · ACTIVE
            </NtText>
          </View>
          <NtText style={styles.paidName}>Voxcraft {plan.name}</NtText>
          <NtText style={styles.paidMeta}>${plan.priceMonthly}/mo · renews monthly</NtText>
          <NtTouchableEffect onPress={goToPlans} style={styles.manageRow}>
            <NtText style={styles.manageText}>Manage plan</NtText>
            <NtMaterialIcon name="chevron-right" color={theme.textDim} size={16} />
          </NtTouchableEffect>
        </View>
      </View>
    );
  }

  // Free → bold upgrade push.
  const next = nextPaidPlan(plan.tier);
  return (
    <View style={styles.wrap}>
      <View style={[styles.boldCard, { backgroundColor: theme.brand, borderColor: theme.brand2 }]}>
        <NtText style={styles.eyebrow}>YOU'RE ON THE FREE PLAN</NtText>
        <NtText style={styles.boldHead}>Unlock the full PBX</NtText>
        <NtText style={styles.benefit}>
          Unlimited extensions, IVR menus, call recording & the AI setup assistant.
        </NtText>
        <NtTouchableEffect onPress={goToPlans} style={styles.upgradeBtn}>
          <NtText style={styles.upgradeText}>
            {next ? `Upgrade to ${next.name} — $${next.priceMonthly}/mo` : "Upgrade"}
          </NtText>
        </NtTouchableEffect>
        <NtTouchableEffect onPress={goToPlans} style={styles.compareRow}>
          <NtText style={styles.compareText}>Compare plans →</NtText>
        </NtTouchableEffect>
      </View>
      <NtText style={styles.currentLabel}>
        Current plan: <NtText style={styles.currentBold}>{plan.name}</NtText>
      </NtText>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    wrap: {
      padding: 10,
      borderTopWidth: 1,
      borderTopColor: t.borderSoft,
    },

    // ---- Paid (Option C) ----
    paidCard: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: 8,
      padding: 12,
    },
    badge: {
      alignSelf: "flex-start",
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      borderWidth: 1,
      borderRadius: 999,
      paddingHorizontal: 8,
      paddingVertical: 3,
      marginBottom: 9,
    },
    badgeDot: { width: 5, height: 5, borderRadius: 3 },
    badgeText: { fontFamily: t.fontMono, fontSize: 9.5, fontWeight: "700", letterSpacing: 0.8 },
    paidName: { color: t.text, fontSize: 13, fontWeight: "700" },
    paidMeta: { color: t.textMute, fontSize: 10.5, marginTop: 3, marginBottom: 11 },
    manageRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingTop: 9,
      borderTopWidth: 1,
      borderTopColor: t.borderSoft,
    },
    manageText: { color: t.textDim, fontSize: 11.5, fontWeight: "600" },

    // ---- Free (Option 3) ----
    boldCard: {
      borderWidth: 1,
      borderRadius: 8,
      padding: 13,
    },
    eyebrow: {
      fontFamily: t.fontMono,
      fontSize: 9,
      fontWeight: "700",
      letterSpacing: 1.2,
      color: "rgba(255,255,255,0.75)",
      marginBottom: 7,
    },
    boldHead: { color: "#ffffff", fontSize: 15, fontWeight: "800", letterSpacing: -0.3, marginBottom: 6 },
    benefit: { color: "rgba(255,255,255,0.85)", fontSize: 11, lineHeight: 16, marginBottom: 12 },
    upgradeBtn: {
      backgroundColor: "#ffffff",
      borderRadius: 6,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
    },
    upgradeText: { color: t.brand, fontSize: 12, fontWeight: "700" },
    compareRow: { alignItems: "center", paddingTop: 8 },
    compareText: { color: "rgba(255,255,255,0.75)", fontSize: 11 },
    currentLabel: { color: t.textMute, fontSize: 10, textAlign: "center", marginTop: 8 },
    currentBold: { color: t.textDim, fontWeight: "700" },
  });

export default NtSidebarPlanCard;
